import { useEffect } from 'react';
import PropTypes from 'prop-types';

/**
 * Manages the Google Translate widget and applies 'notranslate' class to specified elements.
 * This component should be placed in a layout component that is present on all pages.
 *
 * @param {string[]} selectors - An array of CSS selectors (e.g., '#id', '.class') for elements that should NOT be translated.
 * @param {string} includedLanguages - Comma-separated string of language codes to include (e.g., 'en,hi').
 * @param {string} widgetId - The ID of the div where the Google Translate widget will be rendered.
 */
const GoogleTranslateManager = ({ selectors = [], includedLanguages = 'en,hi', widgetId = 'google_translate_element' }) => {

    // Effect for loading the Google Translate script
    useEffect(() => {
        const scriptId = 'google-translate-script';

        // Define the callback function that will be called by the Google Translate script
        window.googleTranslateElementInit = () => {
            new window.google.translate.TranslateElement(
                {
                    pageLanguage: 'en',
                    autoDisplay: false,
                    includedLanguages: includedLanguages,
                    layout: window.google.translate.TranslateElement.InlineLayout.SIMPLE
                },
                widgetId
            );
        };

        // Check if the script is already present to avoid duplicates
        if (!document.getElementById(scriptId)) {
            const script = document.createElement('script');
            script.id = scriptId;
            script.src = `https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit`;
            script.async = true;
            document.head.appendChild(script);
        }

        // Cleanup function to remove the global callback when the component unmounts
        return () => {
            delete window.googleTranslateElementInit;
        };
    }, [includedLanguages, widgetId]);

    // Effect for applying 'notranslate' class to specified elements
    useEffect(() => {
        const elementsToProtect = [];

        selectors.forEach(selector => {
            document.querySelectorAll(selector).forEach(el => {
                el.classList.add('notranslate');
                elementsToProtect.push(el);
            });
        });

        // Cleanup function to remove the 'notranslate' class
        return () => {
            elementsToProtect.forEach(el => el.classList.remove('notranslate'));
        };
    }, [selectors]); // Rerun this effect if the selectors prop changes

    // Effect for hiding the Google Translate top bar and branding
    useEffect(() => {
        const styleId = 'google-translate-style-override';
        const style = document.createElement('style');
        style.id = styleId;
        style.innerHTML = `
            /* Hide the "Powered by Google" logo */
            img.goog-te-gadget-icon { display: none !important; }
        `;
        document.head.appendChild(style);

        return () => {
            document.getElementById(styleId)?.remove();
        };
    }, []);

    // This is a manager component and does not render anything itself.
    return null;
};

GoogleTranslateManager.propTypes = {
    selectors: PropTypes.arrayOf(PropTypes.string),
    includedLanguages: PropTypes.string,
    widgetId: PropTypes.string,
};

export default GoogleTranslateManager;